import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense, useMemo } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum } from '@/app/utils/constants';
import { AuctionIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { getBidDetailsQuery } from '@/app/hooks/queries/useBids';
import { useCvAssessmentDetailsQuery } from '@/app/hooks/queries/useCvAssessment';
import { MainScrollArea } from '@/app/ui/components/main';
import {
  Sidenav,
  SidenavHeader,
  SidenavHeading,
  SidenavItem,
  SidenavLink,
  SidenavList,
} from '@/app/ui/components/sidenav';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';

const CvPreview = lazy(() => import('@/app/components/features/bids/BidCvs/CvPreview'));

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/cv-analysis/other/$positionId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.bids');
  const tCv = useTranslations('components.bidCvs');
  const { bidId, tenderPackageId, projectId, positionId } = Route.useParams();
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  const { data } = useCvAssessmentDetailsQuery({
    bidId,
  });

  const { selectedPosition, otherCvs } = useMemo(() => {
    if (!data) return { selectedPosition: null, otherCvs: [] };

    const uncategorized = data.categories.find(
      (c) => c.tenderCriteriaPosition.id === 'uncategorized',
    );
    const otherCvs = uncategorized ? uncategorized.positions : [];
    const selectedPosition = otherCvs.find((p) => p.id === positionId);

    return { selectedPosition, otherCvs };
  }, [data, positionId]);

  if (!selectedPosition) return null;

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={AuctionIcon} />
                {t('title')}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId"
                params={{ projectId, tenderPackageId, bidId }}
              >
                <span className="line-clamp-1">{bid.bidAdditionalName || bid.companyName}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/cv-analysis"
                params={{ projectId, tenderPackageId, bidId }}
              >
                {tCv('cards.cvAnalysis.title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{tCv('cards.cvAnalysis.cvDetails')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
      </Header>
      <MainScrollArea viewportClassName="max-lg:flex-col flex grow">
        <Sidenav className="border-r xl:w-auto lg:w-auto @7xl:w-68 @5xl:w-56">
          <SidenavHeader className="max-lg:hidden">
            <SidenavHeading> {tCv('labels.positions')}</SidenavHeading>
          </SidenavHeader>
          <SidenavList>
            {otherCvs.map((position, index) => (
              <SidenavItem key={position.id}>
                <SidenavLink
                  to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/cv-analysis/other/$positionId"
                  params={{ projectId, tenderPackageId, bidId, positionId: position.id }}
                >
                  <span className="lg:line-clamp-1">
                    {tCv('labels.numberedCandidate', { number: index + 1 })}
                  </span>
                </SidenavLink>
              </SidenavItem>
            ))}
          </SidenavList>
        </Sidenav>
        <div className="flex flex-col grow min-h-0 max-md:pb-[calc(var(--bottom-navigation-height))]">
          <Suspense fallback={null}>
            <CvPreview documentId={selectedPosition.bidDocId} />
          </Suspense>
        </div>
      </MainScrollArea>
    </>
  );
}
